import { Link } from "react-router-dom";
import {
  SUPPORT_EMAIL, SUPPORT_EMAIL_HREF, PHONE_DISPLAY, PHONE_HREF, PHONE_HOURS,
  SOCIAL_PROFILES,
} from "../../config/site";
import SocialLinks from "./SocialLinks";
import "./ContactDetails.css";

/**
 * How to reach Emporia, in one place.
 *
 * Until 2026-09-27 there were no contact details to show: `SUPPORT_EMAIL`
 * and `PHONE` were null, and every page said only "message us from your
 * Emporia account". That is a real route, but it needs an account — so
 * anyone locked out, or deciding whether to buy at all, had no way to ask.
 *
 * **It still renders nothing if the values are cleared.** The nulls were a
 * deliberate design (a hidden field beats an invented one), and that
 * property is kept rather than assumed away: clearing the config hides the
 * block instead of printing "null".
 *
 * **No opening hours anywhere.** None have been supplied, and a plausible
 * "Mon-Fri, 9-5" is the same mistake as the contact details this replaced:
 * a promise nobody agreed to keep.
 */
const ContactDetails = ({
  variant = "block", showInbox = true, showSocials = true, showHours = true, className = "",
}) => {
  const email = SUPPORT_EMAIL ? (
    <a href={SUPPORT_EMAIL_HREF} className="cd-link">{SUPPORT_EMAIL}</a>
  ) : null;
  // The hours sit next to the number, not in a separate line nobody reads
  // together with it: "0180 191 887" alone makes a caller guess whether
  // anyone is there, and 24/7 is the most useful thing to say.
  //
  // THE SEPARATOR IS A REAL TEXT NODE, and that is the whole point. The first
  // version spaced the badge with `margin-left` alone, which looks right in a
  // browser and is wrong everywhere else: anything reading the TEXT — Google's
  // snippet extractor, a screen reader, copy-and-paste — got
  // "0180 191 88724/7", with the number running straight into the hours. It
  // reached Google's search results that way.
  //
  // "Open 24/7" rather than a bare "24/7" for the same reason: extracted on
  // its own, next to a phone number, a bare token does not say what it is.
  //
  // `showHours={false}` drops the pill AND its separator together, so the
  // line never shows a doubled "·". The footer uses it (owner, 2026-10-13);
  // the hours stay in the structured data and everywhere else.
  const phone = PHONE_DISPLAY ? (
    <>
      <a href={PHONE_HREF} className="cd-link">{PHONE_DISPLAY}</a>
      {PHONE_HOURS && showHours ? (
        <>
          <span className="cd-sep"> · </span>
          <span className="cd-hours">Open {PHONE_HOURS}</span>
        </>
      ) : null}
    </>
  ) : null;

  if (!email && !phone) return null;

  if (variant === "inline") {
    return (
      <span className={`cd-inline ${className}`.trim()}>
        {phone ? <span className="cd-verb">Call </span> : null}
        {phone}
        {phone && email ? <span className="cd-sep"> · </span> : null}
        {email}
      </span>
    );
  }

  return (
    <div className={`cd-block ${className}`.trim()}>
      <ul className="cd-list">
        {/* Every label ends with a real space. NOT the search defect — this
            <li> is `display: flex`, so its children are blockified and a
            crawler's innerText already breaks between them. But textContent,
            copy-and-paste and some screen-reader modes still read
            "Phone0180 191 887", and a trailing space is free. The footer's
            inline variant is the one that actually reached Google. */}
        {phone && <li><span className="cd-label">Phone{" "}</span>{phone}</li>}
        {email && <li><span className="cd-label">Email{" "}</span>{email}</li>}
        {/* The official accounts, by name and handle — the block is where a
            customer checks whether an account claiming to be Emporia is
            real. The footer shows them as icons instead. */}
        {showSocials && SOCIAL_PROFILES && SOCIAL_PROFILES.length > 0 && (
          <li>
            <span className="cd-label">Official accounts{" "}</span>
            <SocialLinks variant="list" />
          </li>
        )}
        {showInbox && (
          <li>
            <span className="cd-label">In your account{" "}</span>
            <Link to="/inbox" className="cd-link">Message us from your Emporia account</Link>
          </li>
        )}
      </ul>
    </div>
  );
};

export default ContactDetails;
